import { FAUNA_CONSERVATION_COLORS, faunaConservationCode } from "./dbcaFauna"
import type {
  Feature,
  FeatureCollection,
  MultiPolygon,
  Point,
  Polygon,
} from "geojson"
import type {
  BorangaFeatureProperties,
  BorangaGeometryKind,
  ContextDocumentType,
  ContextSourceKind,
  DBCATecPecFeature,
  DBCATecPecGeoJSON,
  DBCATecPecProperties,
  DBCAFeature,
  DBCAFeatureProperties,
  DBCAFootprint,
  TecPecGeometryRole,
} from "../types/referenceData"
import { getGeometryFromWkb } from "./parseGISPoint"

/**
 * Conservation status color mapping (hex values for map markers)
 * T = Threatened (red), P1-P4 = Priority 1-4 (gradient from orange to teal)
 */
export const CONSERVATION_HEX_COLORS: Record<string, string> = {
  T: "#DC2626", // Red-600 - Threatened (most critical)
  "1": "#EA580C", // Orange-600 - Priority 1
  "2": "#FBBF24", // Yellow-400 - Priority 2
  "3": "#22C55E", // Green-500 - Priority 3
  "4": "#14B8A6", // Teal-500 - Priority 4
}

export const DEFAULT_CONSERVATION_HEX_COLOR = "#9CA3AF" // Gray-400

/**
 * Conservation status Tailwind classes for badges
 * Includes both background and text color for proper contrast
 */
export const CONSERVATION_BADGE_CLASSES: Record<string, string> = {
  T: "bg-red-600 text-white",
  "1": "bg-orange-500 text-white",
  "2": "bg-yellow-300 text-black",
  "3": "bg-green-500 text-white",
  "4": "bg-teal-500 text-white",
}

export const DEFAULT_CONSERVATION_BADGE_CLASS = "bg-gray-400 text-white"

/**
 * Conservation status order for sorting (Threatened first, then Priority 1-4)
 */
export const CONSERVATION_ORDER: Record<string, number> = {
  T: 0,
  "1": 1,
  "2": 2,
  "3": 3,
  "4": 4,
}

/**
 * Get the sort order for a conservation code
 * Unknown/null codes sort to the end
 */
export function getConservationSortOrder(code: string | undefined): number {
  if (!code) return 999
  return CONSERVATION_ORDER[code] ?? 999
}

/**
 * Format conservation code for display
 * e.g., "1" -> "Priority 1", "T" -> "Threatened"
 */
export function formatConservationCode(code: string | undefined): string {
  if (!code) return "Unknown"
  if (code === "T") return "Threatened"
  if (/^[1-4]$/.test(code)) return `Priority ${code}`
  return code
}

/**
 * Get group name for conservation code (used for grouping in lists)
 * Similar to formatConservationCode but returns "No Conservation Status" for null
 */
export function getConservationGroupName(code: string | undefined): string {
  if (!code) return "No Conservation Status"
  if (code === "T") return "Threatened"
  if (/^[1-4]$/.test(code)) return `Priority ${code}`
  return code
}

/**
 * Get hex color for a conservation code (for map markers)
 */
export function getConservationHexColor(code: string | undefined): string {
  if (!code) return DEFAULT_CONSERVATION_HEX_COLOR
  return (
    CONSERVATION_HEX_COLORS[code] ??
    FAUNA_CONSERVATION_COLORS[faunaConservationCode(code) ?? ""] ??
    DEFAULT_CONSERVATION_HEX_COLOR
  )
}

/**
 * Get Tailwind badge classes for a conservation code
 */
export function getConservationBadgeClass(code: string | undefined): string {
  if (!code) return DEFAULT_CONSERVATION_BADGE_CLASS
  return CONSERVATION_BADGE_CLASSES[code] ?? DEFAULT_CONSERVATION_BADGE_CLASS
}

/**
 * Mapbox expression for conservation-based coloring.
 * Used in Mapbox GL layer paint properties.
 *
 * Note: Cast to ExpressionSpecification when using with Mapbox GL:
 * `conservationColorExpression as ExpressionSpecification`
 */
export const conservationColorExpression = [
  "match",
  ["coalesce", ["get", "faunaColourCode"], ["get", "conservationCode"]],
  "T",
  CONSERVATION_HEX_COLORS["T"],
  "1",
  CONSERVATION_HEX_COLORS["1"],
  "2",
  CONSERVATION_HEX_COLORS["2"],
  "3",
  CONSERVATION_HEX_COLORS["3"],
  "4",
  CONSERVATION_HEX_COLORS["4"],
  ...Object.entries(FAUNA_CONSERVATION_COLORS).flat(),
  DEFAULT_CONSERVATION_HEX_COLOR,
]

/**
 * Format a context source kind for display
 * e.g., "dbca_tpfl" -> "TPFL", "dbca_waherb" -> "WAHerb"
 */
export function formatSourceType(kind: ContextSourceKind): string {
  switch (kind) {
    case "dbca_tpfl":
      return "TPFL"
    case "dbca_waherb":
      return "WAHerb"
    case "dbca_boranga":
      return "Boranga"
    case "dbca_tpfa":
      return "TPFA"
    case "dbca_bc_roost":
      return "Black cockatoo roosts"
    case "dbca_bc_breeding":
      return "Black cockatoo breeding sites"
    case "dbca_tec_pec":
      return "TEC/PEC"
    case "external_csv":
      return "CSV"
    case "external_shapefile":
      return "Shapefile"
    default:
      return kind
  }
}

/**
 * Human-facing title for a context source. DBCA imports get a fixed
 * "DBCA TPFL" / "DBCA WAHerb" label (their filename is noise); other kinds fall
 * back to a provided label (filename / org name) then the kind.
 */
export function formatContextSourceLabel(
  kind: ContextSourceKind,
  fallback?: string | null,
): string {
  switch (kind) {
    case "dbca_tpfl":
      return "DBCA TPFL"
    case "dbca_waherb":
      return "DBCA WAHerb"
    case "dbca_boranga":
      return "DBCA Boranga"
    case "dbca_tpfa":
    case "dbca_bc_roost":
    case "dbca_bc_breeding":
      return `DBCA ${formatSourceType(kind)}`
    default:
      return fallback || formatSourceType(kind)
  }
}

// Organisation document classification. The union lives in
// types/referenceData.ts; these are the display helpers.
export const CONTEXT_DOCUMENT_TYPES: ContextDocumentType[] = [
  "report",
  "procedure_manual",
  "journal_article",
  "other",
]

export const CONTEXT_DOCUMENT_TYPE_LABELS: Record<ContextDocumentType, string> =
  {
    report: "Report",
    procedure_manual: "Procedure manual",
    journal_article: "Journal article",
    other: "Other",
  }

/**
 * Human label for a document type, or null when unset.
 */
export function formatDocumentType(
  documentType: string | null | undefined,
): string | null {
  if (!documentType) return null
  return (
    CONTEXT_DOCUMENT_TYPE_LABELS[documentType as ContextDocumentType] ??
    documentType
  )
}

// The point-species record shape returned by a context_occurrence select with
// the species relationship embedded. `geometry` is a WKB hex string from
// PostgREST; species carries the name + conservation metadata we read taxon and
// conservation status from (never denormalised onto the occurrence row).
export type ContextOccurrenceWithSpecies = {
  id: string
  species_id: string | null
  geometry: string
  // WKB hex of the mapped boundary, when the source provides one (Boranga).
  footprint?: string | null
  properties: DBCAFeatureProperties
  species?: {
    name: string | null
    metadata?: {
      stateConservation?: { WA?: { status?: string | null } | null } | null
    } | null
  } | null
}

/**
 * Rebuild a single DBCA-shaped GeoJSON feature from a context_occurrence row.
 * taxon comes from the species join; conservation status prefers the
 * per-occurrence code in `properties` (the value the source reported) and falls
 * back to the species-derived WA status.
 *
 * A mapped boundary rides along as the `footprint` foreign member (with
 * `hasFootprint` set for layer filters); the feature geometry stays the point.
 */
export function contextOccurrenceToFeature(
  row: ContextOccurrenceWithSpecies,
): DBCAFeature {
  const geometry = getGeometryFromWkb<Point>(row.geometry)
  const footprint = row.footprint
    ? getGeometryFromWkb<DBCAFootprint>(row.footprint)
    : undefined
  const props = row.properties ?? ({} as DBCAFeatureProperties)
  const waStatus = row.species?.metadata?.stateConservation?.WA?.status

  return {
    type: "Feature",
    geometry,
    ...(footprint ? { footprint } : {}),
    properties: {
      ...props,
      taxon: row.species?.name ?? props.taxon,
      speciesId: row.species_id ?? props.speciesId,
      conservationCode: props.conservationCode ?? waStatus ?? undefined,
      ...(props.sourceFormat === "tpfa"
        ? {
            faunaColourCode: faunaConservationCode(
              props.waStatus ?? waStatus ?? undefined,
            ),
          }
        : {}),
      ...(footprint ? { hasFootprint: true } : {}),
    },
  } as DBCAFeature
}

/**
 * Conservation label for an occurrence popup. Boranga reports Threatened flora
 * by IUCN category, so keep that category visible next to the normalised
 * label, e.g. "Threatened (CR)".
 */
export function formatOccurrenceConservation(
  properties: DBCAFeatureProperties,
): string {
  const label = formatConservationCode(properties.conservationCode)
  if (properties.sourceFormat !== "boranga") return label
  const raw = properties.waConservationCode
  return raw && raw !== label && raw !== properties.conservationCode
    ? `${label} (${raw})`
    : label
}

const BORANGA_GEOMETRY_LABELS: Record<BorangaGeometryKind, string> = {
  buffered_point: "Point (boundary not mapped)",
  polygon: "Mapped boundary",
}

/**
 * Label/value rows describing a Boranga occurrence, in display order and with
 * empty values dropped. Shared by the web and mobile map popups.
 */
export function borangaDetailFields(
  properties: BorangaFeatureProperties,
): { label: string; value: string }[] {
  const occurrence = [
    properties.occurrenceNumber,
    properties.occurrenceName && `(${properties.occurrenceName})`,
  ]
    .filter(Boolean)
    .join(" ")
  const rows: [string, string | number | undefined][] = [
    ["Occurrence", occurrence],
    ["Common name", properties.commonName],
    ["Location", properties.locationDescription],
    ["District", properties.district],
    ["Observation date", properties.observationDate],
    ["Plants alive", properties.plantsAlive],
    ["Mature plants alive", properties.plantsAliveMature],
    ["Count comments", properties.plantCountComments],
    ["In flower", properties.inFlower],
    ["Plant condition", properties.plantCondition],
    ["Survey area (m²)", properties.surveyAreaSqm || undefined],
    ["Geometry", BORANGA_GEOMETRY_LABELS[properties.geometryKind]],
    ["Location accuracy", properties.locationAccuracy],
    ["Coordinate source", properties.coordinateSource],
    ["Commonwealth status", properties.commonwealthConservationCode],
    ["Wild status", properties.wildStatus],
  ]
  return rows.flatMap(([label, value]) =>
    value === undefined || value === ""
      ? []
      : [{ label, value: String(value) }],
  )
}

/** Zoom at which occurrence footprints replace their point markers. */
export const DBCA_FOOTPRINT_MIN_ZOOM = 14

/**
 * The mapped boundaries of the given occurrence features, as polygon features
 * carrying the same properties (so popups and colouring work unchanged).
 */
export function dbcaFootprintFeatures(
  features: readonly (Feature<Point, DBCAFeatureProperties> & {
    footprint?: DBCAFootprint
  })[],
): FeatureCollection<DBCAFootprint, DBCAFeatureProperties> {
  const footprints: Feature<DBCAFootprint, DBCAFeatureProperties>[] = []
  for (const feature of features) {
    if (feature.footprint) {
      footprints.push({
        type: "Feature",
        geometry: feature.footprint,
        properties: feature.properties,
      })
    }
  }
  return { type: "FeatureCollection", features: footprints }
}

/**
 * Rebuild a GeoJSON FeatureCollection from context_occurrence rows. Shared by
 * the web app and mobile so reconstruction lives in exactly one place.
 */
export function contextOccurrencesToGeoJSON(
  rows: ContextOccurrenceWithSpecies[],
): FeatureCollection<Point, DBCAFeatureProperties> {
  return {
    type: "FeatureCollection",
    features: rows.map(contextOccurrenceToFeature),
  }
}

export type ContextAreaRow = {
  id: string
  context_source_id: string
  label: string | null
  class_code: string | null
  geometry: string
  properties: DBCATecPecProperties
}

export type ContextAreaConversionError = {
  id: string
  message: string
}

export type ContextAreasGeoJSONResult = {
  geojson: DBCATecPecGeoJSON
  rejected: ContextAreaConversionError[]
}

export function isTecPecGeometryRole(
  value: unknown,
): value is TecPecGeometryRole {
  return value === "boundary" || value === "buffer"
}

export function tecPecRoleLabel(role: TecPecGeometryRole | null | undefined) {
  if (role === "boundary") return "Boundary"
  if (role === "buffer") return "Buffered area"
  return
}

/**
 * Mapbox filter for TEC/PEC polygons imported without a geometry role. These
 * have no role toggle, so they render whenever the source is shown.
 *
 * Note: Cast to ExpressionSpecification when using with Mapbox GL.
 */
export const TEC_PEC_UNSPECIFIED_ROLE_FILTER = [
  "match",
  ["get", "geometry_role"],
  ["boundary", "buffer"],
  false,
  true,
]

export type ContextAreaCommunitySummary = {
  communityId: string
  communityName: string | null
  instanceCount: number
  boundaryCount: number
  bufferCount: number
  areas: DBCATecPecFeature[]
}

function contextAreaCommunityId(feature: DBCATecPecFeature): string {
  const properties = feature.properties
  const communityId =
    properties.COM_ID ?? properties.class_code ?? properties.label
  return (
    String(communityId ?? "Unknown community").trim() || "Unknown community"
  )
}

function contextAreaInstanceId(feature: DBCATecPecFeature): string {
  const properties = feature.properties
  const instanceId = properties.OCC_UNIQUE ?? properties.BDY_ID ?? feature.id
  return String(instanceId ?? feature.id)
}

export function aggregateContextAreasByCommunity(
  features: DBCATecPecFeature[],
): ContextAreaCommunitySummary[] {
  const byCommunity = new Map<string, DBCATecPecFeature[]>()
  for (const feature of features) {
    const communityId = contextAreaCommunityId(feature)
    const grouped = byCommunity.get(communityId) ?? []
    grouped.push(feature)
    byCommunity.set(communityId, grouped)
  }

  return Array.from(byCommunity, ([communityId, areas]) => {
    const instanceIds = new Set(areas.map(contextAreaInstanceId))
    const first = areas[0]?.properties
    return {
      communityId,
      communityName: first?.COM_NAME ?? first?.label ?? null,
      instanceCount: instanceIds.size,
      boundaryCount: areas.filter(
        (feature) => feature.properties.geometry_role === "boundary",
      ).length,
      bufferCount: areas.filter(
        (feature) => feature.properties.geometry_role === "buffer",
      ).length,
      areas,
    }
  })
}

/** Rebuild a polygon GeoJSON feature from a PostgREST context_area row. */
export function contextAreaToFeature(row: ContextAreaRow): DBCATecPecFeature {
  const sourceProperties: Partial<DBCATecPecProperties> = row.properties ?? {}
  // The importer writes null when the filename didn't give a role. A missing
  // key means the properties aren't a context area's at all.
  const geometryRole = sourceProperties.geometry_role
  if (geometryRole !== null && !isTecPecGeometryRole(geometryRole)) {
    throw new Error(
      `Context area ${row.id} is missing a valid TEC/PEC geometry role`,
    )
  }

  return {
    type: "Feature",
    id: row.id,
    geometry: getGeometryFromWkb<Polygon | MultiPolygon>(row.geometry),
    properties: {
      ...sourceProperties,
      geometry_role: geometryRole,
      context_area_id: row.id,
      context_source_id: row.context_source_id,
      label: row.label,
      class_code: row.class_code,
    },
  }
}

/** Rebuild all polygon rows while retaining valid rows when one row is bad. */
export function contextAreasToGeoJSONSafe(
  rows: ContextAreaRow[],
): ContextAreasGeoJSONResult {
  const features: DBCATecPecFeature[] = []
  const rejected: ContextAreaConversionError[] = []

  for (const row of rows) {
    try {
      features.push(contextAreaToFeature(row))
    } catch (error) {
      rejected.push({
        id: row.id,
        message: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return {
    geojson: { type: "FeatureCollection", features },
    rejected,
  }
}

// The point-species record shape returned by a document_occurrence select with
// the species relationship embedded. Unlike context_occurrence, document rows
// carry the raw extracted taxon + conservation code as columns, and `geometry`
// (a WKB hex string) is NULLABLE — un-locatable records are retained. Rows with
// a null geometry are skipped by the adapters below.
export type DocumentOccurrenceWithSpecies = {
  id: string
  species_id: string | null
  taxon: string | null
  conservation_code: string | null
  geometry: string | null
  properties: DBCAFeatureProperties | null
  species?: {
    name: string | null
    metadata?: {
      stateConservation?: { WA?: { status?: string | null } | null } | null
    } | null
  } | null
}

/**
 * Rebuild a single DBCA-shaped GeoJSON feature from a document_occurrence row,
 * or null when the row has no resolved geometry. taxon prefers the species join
 * then the raw extracted taxon; conservation status prefers the extracted
 * conservation_code, then the properties bag, then the species-derived WA status.
 */
export function documentOccurrenceToFeature(
  row: DocumentOccurrenceWithSpecies,
): DBCAFeature | null {
  if (!row.geometry) return null
  const geometry = getGeometryFromWkb<Point>(row.geometry)
  const props = row.properties ?? ({} as DBCAFeatureProperties)
  const waStatus = row.species?.metadata?.stateConservation?.WA?.status

  return {
    type: "Feature",
    geometry,
    properties: {
      ...props,
      taxon: row.species?.name ?? row.taxon ?? props.taxon,
      speciesId: row.species_id ?? props.speciesId,
      conservationCode:
        row.conservation_code ??
        props.conservationCode ??
        waStatus ??
        undefined,
    },
  } as DBCAFeature
}

/**
 * Rebuild a GeoJSON FeatureCollection from document_occurrence rows, skipping
 * rows without a resolved geometry. Shared by web and mobile.
 */
export function documentOccurrencesToGeoJSON(
  rows: DocumentOccurrenceWithSpecies[],
): FeatureCollection<Point, DBCAFeatureProperties> {
  const features: DBCAFeature[] = []
  for (const row of rows) {
    const feature = documentOccurrenceToFeature(row)
    if (feature) features.push(feature)
  }
  return {
    type: "FeatureCollection",
    features,
  }
}

/**
 * Format source format for display (lowercase version from feature properties)
 * e.g., "tpfl" -> "TPFL", "waherb" -> "WAHerb"
 */
export function formatSourceFormat(sourceFormat: string): string {
  switch (sourceFormat.toLowerCase()) {
    case "tpfl":
      return "TPFL"
    case "waherb":
      return "WAHerb"
    case "tpfa":
      return "TPFA"
    case "bc_roost":
      return "Black cockatoo roost"
    case "bc_breeding":
      return "Black cockatoo breeding site"
    case "boranga":
      return "Boranga"
    default:
      return sourceFormat
  }
}
